// ─── Live → Sensor Readings ──────────────────────────────────────────────────
// Mirrors the XD reference: icon-tabs at the top, then one sensor group per
// configured sensor on the device. Within a group, each parameter gets a
// two-card row: a left "value card" with the parameter name + current
// reading, and a right "sparkline card" with the last 24h of data.
//
// Data sources:
//   - useDeviceStore.config.sensors → sensor + parameter catalogue
//   - projectsApi.fetchGraphData(parameter_id, {fromDate, toDate, timezone})
//     → time-series points, newest = the displayed value
//   - useAuthStore.user.timezone → resolved via resolveIanaTz so the
//     series matches what Visualize / Reports show
//
// Sparklines are Recharts LineCharts — already bundled, no new deps. The
// XD design ranges the Y axis between the min and max of the visible
// window, so we drive that explicitly instead of letting Recharts pad.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader, AlertCircle, CheckCircle2, Thermometer, Droplet, Cloud, Play, Square } from 'lucide-react'
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useDeviceStore } from '../../store/device-store'
import { projectsApi, enableLiveStream, disableLiveStream, fetchLiveReadings } from '../../lib/projectsApi'
import useAuthStore from '../../store/auth-store'
import { resolveIanaTz } from '../../lib/tz'
import { useDashboardContext } from '../../context/DashboardContext'
import LiveIconTabs from './LiveIconTabs'

const Card = ({ children, className = '' }) => (
  <div className={`bg-white border border-gray-200 rounded-lg shadow-sm px-2 py-2 ${className}`}>
    {children}
  </div>
)

// Sparkline-colour rotation; loops if a sensor has more than 6 parameters.
const SPARK_COLORS = ['#f59e0b', '#10b981', '#8b5cf6', '#3b82f6', '#ef4444', '#06b6d4']

// Live-stream tuning (device command 81). We send ONLY the streaming flag — the
// rest of the args are optional and the logger uses its defaults (all sensors;
// default rate; 300s inactivity timeout). We poll the server every few seconds
// and re-arm well before that 300s timeout so the stream stays alive while the
// tab is open.
const STREAM_POLL_MS   = 5000
const STREAM_KEEPALIVE_MS   = 120000   // < device's 300s inactivity so it never times out
const LIVE_POINT_CAP   = 60            // live points kept per parameter sparkline
const STREAM_STALE_SEC = 30            // ignore readings older than this (device stopped streaming)

// Picks an icon based on the parameter name — purely cosmetic, falls
// back to a generic droplet if no keyword matches.
const paramIcon = (name) => {
  const n = (name || '').toLowerCase()
  if (n.includes('temp'))                              return Thermometer
  if (n.includes('rain') || n.includes('precip'))      return Cloud
  return Droplet
}

// Number formatter — uses the parameter's configured decimal places
// (`precision`) so Live readings match the dashboard, charts and reports
// instead of a hardcoded 2dp. Em-dash when the value isn't present yet (device
// hasn't reported, query 404'd, etc.). Falls back to the old 2dp-float /
// integer-plain rendering only when no precision is available.
const fmtValue = (v, precision) => {
  if (v == null || v === '') return '—'
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  if (Number.isFinite(precision)) return n.toFixed(precision)
  if (Number.isInteger(n)) return String(n)
  return n.toFixed(2)
}

// One parameter row: value card on the left, sparkline card on the right.
// `series` is an array of { x: ms-epoch, y: number } in ascending time.
// How many slots the live "strip chart" window shows. Newest point sits near the
// right edge with one spare slot, so the graph reads as "one from full"; a new
// value fills it and the whole line slides left — a continuously-updating scroll.
const STRIP_WINDOW = 30

function ParameterRow({ parameter, series, color, windowed = false }) {
  const Icon = paramIcon(parameter.name)
  const latest = series.length ? series[series.length - 1].y : null

  // While streaming, plot a fixed-width scrolling window on an INDEX x-axis so
  // spacing stays constant and new points push in from the right (the line
  // slides left). Otherwise plot the full series against its real timestamps.
  const { chartData, xKey, xType, xDomain } = useMemo(() => {
    if (!windowed) {
      return { chartData: series, xKey: 'x', xType: 'category', xDomain: ['dataMin', 'dataMax'] }
    }
    const pts = series.slice(-STRIP_WINDOW)
    const offset = STRIP_WINDOW - pts.length   // right-align the line (grows from the right)
    const data = pts.map((p, i) => ({ ...p, ix: offset + i }))
    // Domain runs one slot past the last index → a spare slot on the right.
    return { chartData: data, xKey: 'ix', xType: 'number', xDomain: [0, STRIP_WINDOW] }
  }, [series, windowed])

  // Y-domain derived from the visible data so the sparkline fills the card
  // vertically. Falls back to a small spread when there's only one point.
  const [yMin, yMax] = useMemo(() => {
    const ys = chartData.map((p) => p.y).filter((n) => Number.isFinite(n))
    if (!ys.length) return [0, 1]
    const lo = Math.min(...ys), hi = Math.max(...ys)
    if (lo === hi) return [lo - 1, hi + 1]
    const pad = (hi - lo) * 0.1
    return [lo - pad, hi + pad]
  }, [chartData])
  // Axis min/max labels respect the parameter's precision but strip trailing
  // zeros so the narrow axis stays compact (e.g. 36.520000 -> 36.52).
  const yLabel = (n) => {
    if (!Number.isFinite(n)) return ''
    const prec = Number.isFinite(parameter.precision) ? parameter.precision : 2
    return Number(n.toFixed(prec)).toString()
  }

  return (
    <div className="mob-sz grid grid-cols-[minmax(100px,1fr)_3fr] gap-4 items-stretch mb-4">
      {/* Value card */}
      <Card className="flex flex-col justify-center">
        <p className="common-size12 text-xs font-bold text-[#4D4D4D] mb-4 flex items-center gap-2">
          <Icon className="w-4 h-4 text-gray-400" />
          {parameter.name}{parameter.unit ? ` (${parameter.unit})` : ''}
        </p>
        <p className="common-size50 text-3xl text-center font-bold text-[#000000]">{fmtValue(latest, parameter.precision)}</p>
      </Card>

      {/* Sparkline card. The Y axis is rendered (narrow, just labels — no
          line) so the user sees the visible range, matching the XD design. */}
      <Card className="!py-2">
        {chartData.length === 0 ? (
          <div className="h-24 flex items-center justify-center text-xs text-gray-400">No recent data</div>
        ) : (
          <ResponsiveContainer width="100%" height={96}>
            <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
              <XAxis dataKey={xKey} type={xType} domain={xDomain} hide allowDecimals={false} />
              <YAxis
                domain={[yMin, yMax]}
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                width={32}
                ticks={[yMax, yMin]}
                tickFormatter={yLabel}
              />
              <Tooltip
                isAnimationActive={false}
                cursor={{ stroke: '#cbd5e1', strokeWidth: 1 }}
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null
                  const pt = payload[0].payload
                  const when = Number.isFinite(pt.x) ? new Date(pt.x).toLocaleString() : ''
                  return (
                    <div className="bg-white border border-gray-200 rounded shadow-sm px-2 py-1 text-[11px] leading-tight text-[#4D4D4D]">
                      <div className="font-semibold">{fmtValue(pt.y, parameter.precision)}{parameter.unit ? ` ${parameter.unit}` : ''}</div>
                      {when && <div className="text-gray-400">{when}</div>}
                    </div>
                  )
                }}
              />
              <Line
                type="linear" dataKey="y" stroke={color}
                strokeWidth={1.8} dot={{ r: 2.5, fill: color, strokeWidth: 0 }}
                isAnimationActive={false} connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  )
}

export default function SensorReadingsTab({ projectId, deviceId, currentLiveIndex, setCurrentLiveIndex, devices = [], onSelectDevice, showHeader = true, streamSlot = null, onRequestEditUpdate }) {
  const { device, config, isLoading, error, loadDevice } = useDeviceStore()
  // Bumped by the dashboard auto-refresh timer (and the manual Refresh button).
  // Added to the baseline-fetch deps below so the Live readings re-pull on each
  // auto-refresh cycle, same as the dashboard tiles/chart.
  const { dataRefreshNonce = 0 } = useDashboardContext()

  // Live streaming needs an MQTT-capable logger. Older/non-MQTT loggers (e.g. X3)
  // can't stream, so we show a firmware-update prompt instead of the stream UI.
  const mqttReady = device?.mqtt_enabled !== false   // undefined (not loaded yet) ≠ "not ready"
  const rawTz = useAuthStore((s) => s.user?.timezone)
  const customerTz = useMemo(() => resolveIanaTz(rawTz), [rawTz])
  // Series cache, keyed by parameter id, populated by the effect below.
  const [seriesByParam, setSeriesByParam] = useState({})
  // Live points streamed this session (device command 81 → MQTT CBOR → poll),
  // keyed by parameter id. Appended to the historical series for display.
  const [liveByParam, setLiveByParam] = useState({})

  useEffect(() => {
    if (projectId && deviceId) loadDevice(projectId, deviceId)
  }, [projectId, deviceId, loadDevice])

  // ── Live streaming (device command 81) — explicit Start/Stop ─────────────────
  // Streaming no longer auto-starts; the user presses "Start Reading". While
  // active we poll the latest decoded values, keep the stream alive (re-arm cmd
  // 81 before its inactivity timeout), and count the samples received. Switching
  // device or leaving the tab stops it. Degrades gracefully — if the device
  // isn't MQTT/streaming-capable the poll just returns nothing.
  const [isStreaming, setIsStreaming] = useState(false)
  const [sampleCount, setSampleCount] = useState(0)
  // Transient feedback for the explicit Start/Stop presses, so it's visible that
  // cmd 81 actually ran (and failures surface instead of being swallowed).
  // { text, error } — auto-cleared a few seconds after it's set.
  const [streamMsg, setStreamMsg] = useState(null)
  const pollRef = useRef(null)
  const keepRef = useRef(null)
  const lastSampleRef = useRef(null)

  useEffect(() => {
    if (!streamMsg) return
    const t = setTimeout(() => setStreamMsg(null), 5000)
    return () => clearTimeout(t)
  }, [streamMsg])

  // `silent` teardown (device switch / unmount) never shows a banner — a stop
  // that fails there (e.g. the device we're leaving no longer resolves) is
  // harmless. Only an explicit "Stop Reading" press reports its result.
  const stopStreaming = useCallback(async ({ silent = false } = {}) => {
    const wasStreaming = !!pollRef.current
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    if (keepRef.current) { clearInterval(keepRef.current); keepRef.current = null }
    setIsStreaming(false)
    // Nothing to stop if we never started — don't send a spurious cmd 81.
    if (!wasStreaming || !projectId || !deviceId) return
    try {
      const res = await disableLiveStream(projectId, deviceId)
      if (!silent) setStreamMsg({ text: `Stop sent — cmd 81 "${res?.data ?? '0'}"${res?.command_queue_id ? ` (queue #${res.command_queue_id})` : ''}`, error: false })
    } catch (e) {
      if (!silent) setStreamMsg({ text: `Failed to send stop (cmd 81): ${e?.response?.data?.error || e.message || 'request failed'}`, error: true })
    }
  }, [projectId, deviceId])

  const startStreaming = useCallback(async () => {
    if (!projectId || !deviceId || pollRef.current) return
    setIsStreaming(true)
    setSampleCount(0)
    setLiveByParam({})
    setStreamMsg(null)
    lastSampleRef.current = null

    // Keep-alive re-arm (silent): fires every STREAM_KEEPALIVE_MS so the device's
    // inactivity timeout doesn't stop the stream. Only the explicit press below
    // reports success/failure.
    const arm = () => enableLiveStream(projectId, deviceId).catch(() => {})
    const poll = async () => {
      try {
        const res = await fetchLiveReadings(projectId, deviceId)
        if (!res || !Array.isArray(res.readings)) return
        // Readings fresh enough to count as "live".
        const fresh = res.readings.filter((r) =>
          r.parameter_id != null && r.value != null &&
          (r.age_sec == null || r.age_sec <= STREAM_STALE_SEC))
        // Sample # = count of new frames since Start — tracked by the device's
        // sample_no when present, else one per fresh poll.
        const nos = fresh.map((r) => r.sample_no).filter((n) => n != null)
        if (nos.length) {
          const maxNo = Math.max(...nos)
          if (lastSampleRef.current !== maxNo) {
            lastSampleRef.current = maxNo
            setSampleCount((c) => c + 1)
          }
        } else if (fresh.length) {
          setSampleCount((c) => c + 1)
        }
        setLiveByParam((prev) => {
          const next = { ...prev }
          for (const r of fresh) {
            const pid = r.parameter_id
            const y = Number(r.value)
            if (!Number.isFinite(y)) continue
            const x = r.updated_at ? new Date(r.updated_at).getTime() : Date.now()
            const arr = next[pid] ? next[pid].slice() : []
            const last = arr[arr.length - 1]
            const dupe = last && (
              (last.sample_no != null && last.sample_no === r.sample_no)
              || (last.x === x && last.y === y)
            )
            if (!dupe) {
              arr.push({ x, y, sample_no: r.sample_no })
              if (arr.length > LIVE_POINT_CAP) arr.splice(0, arr.length - LIVE_POINT_CAP)
              next[pid] = arr
            }
          }
          return next
        })
      } catch { /* transient — next tick retries */ }
    }

    // Initial press is AWAITED so we can confirm cmd 81 was queued and surface a
    // failure instead of leaving the UI stuck on "Streaming…" with nothing sent.
    try {
      const res = await enableLiveStream(projectId, deviceId)
      setStreamMsg({ text: `Start sent — cmd 81 "${res?.data ?? '1'}"${res?.command_queue_id ? ` (queue #${res.command_queue_id})` : ''}`, error: false })
    } catch (e) {
      setIsStreaming(false)
      setStreamMsg({ text: `Failed to start streaming (cmd 81): ${e?.response?.data?.error || e.message || 'request failed'}`, error: true })
      return
    }
    poll()
    pollRef.current = setInterval(poll, STREAM_POLL_MS)
    keepRef.current = setInterval(arm, STREAM_KEEPALIVE_MS)
  }, [projectId, deviceId])

  // Stop streaming when the device changes or the tab unmounts.
  useEffect(() => {
    return () => { stopStreaming({ silent: true }) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, deviceId])

  // Pull the last 24h of data for every parameter on the device. We do this
  // in parallel so the sparklines fill in together rather than one at a
  // time. Cancelled on unmount / deviceId change so a slow API call doesn't
  // stomp on a newer one's result.
  const sensors = config?.sensors || []
  const allParamIds = useMemo(
    () => sensors.flatMap((s) => (s.parameters || []).map((p) => p.id)),
    [sensors]
  )
  const paramKey = allParamIds.join(',')

  useEffect(() => {
    if (!allParamIds.length) { setSeriesByParam({}); return }
    let cancelled = false
    const pad = (n) => String(n).padStart(2, '0')
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    const now      = new Date()
    const dayAgo   = new Date(now.getTime() - 24 * 60 * 60 * 1000)
    const fromDate = fmt(dayAgo)
    const toDate   = fmt(now)

    Promise.all(
      allParamIds.map((pid) =>
        projectsApi.fetchGraphData(Number(pid), { fromDate, toDate, timezone: customerTz })
          .then((res) => ({ pid, points: Array.isArray(res?.data) ? res.data : [] }))
          .catch(() => ({ pid, points: [] }))
      )
    ).then((results) => {
      if (cancelled) return
      const next = {}
      for (const { pid, points } of results) {
        // Coerce { x, y } to numeric ms-epoch + number; drop bad points.
        next[pid] = points
          .map((p) => ({
            x: typeof p.x === 'string' ? new Date(p.x).getTime() : Number(p.x),
            y: p.y == null ? null : Number(p.y),
          }))
          .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
          .sort((a, b) => a.x - b.x)
      }
      setSeriesByParam(next)
    })
    return () => { cancelled = true }
    // dataRefreshNonce re-pulls the 24h baseline on each auto-refresh cycle so
    // the value cards + sparklines update without the user clicking "Start Reading".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramKey, customerTz, dataRefreshNonce])

  // Rendered in every state so the device switcher stays visible while loading.
  // showHeader={false} when the parent renders the shared header outside the
  // read-only gate so the nav/device switcher stay clickable.
  const header = showHeader ? (
    <Card className="!py-3">
      <LiveIconTabs
        currentLiveIndex={currentLiveIndex}
        setCurrentLiveIndex={setCurrentLiveIndex}
        devices={devices}
        deviceId={deviceId}
        onSelectDevice={onSelectDevice}
      />
    </Card>
  ) : null

  if (!deviceId) {
    return (
      <div className="w-full h-full flex flex-col bg-[#F1F2F4] overflow-y-auto gap-5">
        {header}
        <div className="p-6 text-sm text-gray-500">Select a device to view its sensor readings.</div>
      </div>
    )
  }
  if (isLoading) {
    return (
      <div className="w-full h-full flex flex-col bg-[#F1F2F4] overflow-y-auto gap-5">
        {header}
        <div className="p-6 flex items-center gap-2 text-sm text-gray-500"><Loader className="w-4 h-4 animate-spin" /> Loading device…</div>
      </div>
    )
  }

  // Live Data streaming controls: Start/Stop (device command 81) + the running
  // count of samples received since the stream started. Per the XD these live in
  // the icon-tabs header row, so when a slot is provided we portal them there;
  // otherwise (e.g. mobile, own-header) they render inline below the header.
  const streamControls = (
    <div className="flex items-center gap-3 flex-wrap">
      <button
        type="button"
        onClick={isStreaming ? () => stopStreaming() : startStreaming}
        className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition ${
          isStreaming
            ? 'bg-white border border-red-400 text-red-600 hover:bg-red-50'
            : 'bg-[#358ECD] hover:bg-[#2d7ab5] text-white'
        }`}
      >
        {isStreaming
          ? (<><Square className="w-3.5 h-3.5" /> Stop Reading</>)
          : (<><Play className="w-3.5 h-3.5" /> Start Reading</>)}
      </button>
      <span className="common-size15 text-sm text-[#4D4D4D] whitespace-nowrap">
        Sample #: <span className="font-semibold">{sampleCount}</span>
      </span>
      {isStreaming && (
        <span className="inline-flex items-center gap-1 text-xs text-green-600">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Streaming…
        </span>
      )}
    </div>
  )

  return (
    <div className="w-full h-full flex flex-col bg-[#F1F2F4] overflow-y-auto gap-5">
      {header}

      {streamSlot
        ? createPortal(streamControls, streamSlot)
        : <Card className="!py-3">{streamControls}</Card>}

      {error && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Confirmation / error for the last Start or Stop press — shows cmd 81 did run. */}
      {streamMsg && (
        <div className={`px-3 py-2 rounded text-sm flex items-center gap-2 border ${
          streamMsg.error
            ? 'bg-red-50 border-red-200 text-red-700'
            : 'bg-blue-50 border-blue-200 text-blue-700'
        }`}>
          {streamMsg.error ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />} {streamMsg.text}
        </div>
      )}

      {/* Non-MQTT logger: Live Readings isn't available — prompt a firmware
          update instead of the stream UI (matches the XD empty state). */}
      {!mqttReady ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-24 gap-5">
          <p className="common-size18 text-base text-gray-500">To access this feature, update your data logger firmware.</p>
          <button
            type="button"
            onClick={() => onRequestEditUpdate?.()}
            className="px-6 py-2.5 bg-[#358ECD] hover:bg-[#2d7ab5] text-white rounded-md text-sm font-semibold uppercase tracking-wide transition"
          >
            Update to Latest
          </button>
        </div>
      ) : (<>

      {sensors.length === 0 && (
        <Card>
          <div className="text-sm text-gray-500 italic">No sensors configured on this device.</div>
        </Card>
      )}

      {/* One group per sensor. Sensor name renders as a label-only card
          (matches the XD reference), with each parameter as a value+chart
          row underneath. */}
      {sensors.map((sensor) => (
        <div key={sensor.id} className="flex flex-col bg-white rounded-lg p-6">
          <Card className="!py-3 px-0 py-0 border-b pb-4 mb-4">
            <h3 className="common-size18 text-sm font-semibold text-[#4D4D4D]">{sensor.name}</h3>
          </Card>
          {(sensor.parameters || []).map((p, idx) => (
            <ParameterRow
              key={p.id}
              parameter={p}
              // While streaming: a scrolling live window (seeded with the tail of
              // the 24h baseline so it isn't blank at start) that new points push
              // through — a constantly-updating strip chart. Otherwise: the full
              // 24h baseline + any live points. The last point drives the big value.
              series={isStreaming
                ? [...(seriesByParam[p.id] || []).slice(-STRIP_WINDOW), ...(liveByParam[p.id] || [])]
                : [...(seriesByParam[p.id] || []), ...(liveByParam[p.id] || [])]}
              windowed={isStreaming}
              color={SPARK_COLORS[idx % SPARK_COLORS.length]}
            />
          ))}
          {(sensor.parameters || []).length === 0 && (
            <Card>
              <div className="text-xs text-gray-400 italic">No parameters configured on this sensor.</div>
            </Card>
          )}
        </div>
      ))}
      </>)}
    </div>
  )
}
